import { useEffect, useRef, useState, useCallback } from 'react'
import { cable } from '@/lib/cable'
import type { Subscription } from '@rails/actioncable'
import type { Suggestion, AiDraftSuggestion } from '@/components/inbox/SuggestionBar'

interface SuggestionsResponse {
  suggestions: Suggestion[]
  all: Suggestion[]
  ai_draft: AiDraftSuggestion | null
}

interface BroadcastPayload {
  type: 'ai_draft.created'
  conversation_id: string
  draft: AiDraftSuggestion & { id: string; prompt_key: string }
}

/**
 * Fetches ranked canned-answer chips for a conversation and subscribes to
 * SuggestionsChannel for AI-drafted updates.
 */
export function useConversationSuggestions(accountSlug: string | undefined, conversationId: string | undefined) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [allSuggestions, setAllSuggestions] = useState<Suggestion[]>([])
  const [aiDraft, setAiDraft] = useState<AiDraftSuggestion | null>(null)
  const subRef = useRef<Subscription | null>(null)

  // Initial fetch
  useEffect(() => {
    if (!accountSlug || !conversationId) return
    let cancelled = false

    fetch(`/${accountSlug}/conversations/${conversationId}/suggestions`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(r => (r.ok ? r.json() : null))
      .then((data: SuggestionsResponse | null) => {
        if (cancelled || !data) return
        setSuggestions(data.suggestions || [])
        setAllSuggestions(data.all || [])
        setAiDraft(data.ai_draft || null)
      })
      .catch(() => {/* swallow — composer still usable */})

    return () => { cancelled = true }
  }, [accountSlug, conversationId])

  // Cable subscription for AI drafts streaming in
  useEffect(() => {
    if (!conversationId) return

    subRef.current = cable.subscriptions.create(
      { channel: 'SuggestionsChannel' },
      {
        received(data: BroadcastPayload) {
          if (data.type !== 'ai_draft.created') return
          if (data.conversation_id !== conversationId) return
          setAiDraft(data.draft)
        }
      }
    )

    return () => {
      subRef.current?.unsubscribe()
      subRef.current = null
    }
  }, [conversationId])

  const clearDraft = useCallback(() => setAiDraft(null), [])

  return { suggestions, allSuggestions, aiDraft, clearDraft }
}
